export type FeatureFlagDefinition = {
    default: boolean;
    description: string;
};

export const FEATURE_FLAGS = {
    multiCollateral: {
        default: false,
        description: "Allow selecting collateral when topping up or withdrawing"
    },
    lockbox: {
        default: true,
        description: "Enable lockbox features"
    },
    showVaultWithdrawSlippage: {
        default: false,
        description: "Show slippage controls on vault withdrawals"
    },
    currentlyDownForMaintenance: {
        default: false,
        description: "Show maintenance page and skip data prefetch"
    },
    leaderboard: {
        default: false,
        description: "Show points leaderboard and fetch leaderboard data"
    }
} as const satisfies Record<string, FeatureFlagDefinition>;

export type FeatureFlagName = keyof typeof FEATURE_FLAGS;

const FEATURE_FLAG_STORAGE_KEY = "loopscale_feature_flag_overrides";

type Overrides = Partial<Record<FeatureFlagName, boolean>>;

function readOverrides(): Overrides {
    if (typeof window === "undefined") return {};
    try {
        const raw = window.localStorage.getItem(FEATURE_FLAG_STORAGE_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
        return {};
    }
}

function writeOverrides(overrides: Overrides): void {
    if (typeof window === "undefined") return;
    try {
        if (Object.keys(overrides).length === 0) {
            window.localStorage.removeItem(FEATURE_FLAG_STORAGE_KEY);
            return;
        }
        window.localStorage.setItem(FEATURE_FLAG_STORAGE_KEY, JSON.stringify(overrides));
    } catch {
        // localStorage can throw SecurityError (Safari private mode), QuotaExceededError, or in sandboxed iframes.
    }
}

export function getFeatureFlag(name: FeatureFlagName): boolean {
    const overrides = readOverrides();
    if (name in overrides && typeof overrides[name] === "boolean") {
        return overrides[name] as boolean;
    }
    return FEATURE_FLAGS[name].default;
}

export function getFeatureFlagOverrides(): Overrides {
    return readOverrides();
}

export function setFeatureFlagOverride(name: FeatureFlagName, value: boolean | null): void {
    const overrides = readOverrides();
    if (value === null) {
        delete overrides[name];
    } else {
        overrides[name] = value;
    }
    writeOverrides(overrides);
}

export function clearFeatureFlagOverrides(): void {
    writeOverrides({});
}
